import React, { useEffect, useState, useRef } from 'react';
import {
  DsExplorerLink,
  DsTag,
  DsSparkline,
  DsDialog,
  DsLineOverTime,
  DsInputDateRange,
  DsIcon,
  DsLogo,
} from '@tokenyico/design-system/dist/reactJS';
import { useTranslation } from 'react-i18next';

import { currencyFormat, ethereumNetworkInfo } from '@tokenyico/design-system';
import { hasValuation } from 'app/helpers/hasValuation';
import { TokenService } from 'services';

const DashboardTokenInformation = ({ token, valuation }) => {
  const [points, setPoints] = useState([]);
  const [sparklinePoints, setSparklinePoints] = useState([]);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [minDate, setMinDate] = useState('');
  const [maxDate, setMaxDate] = useState('');
  const [isNavOpen, setIsNavOpen] = useState(false);

  const navDialogRef = useRef();

  const { t } = useTranslation();

  const mapValuationPoints = (_valuation) => {
    return _valuation.history
      .filter(({ date, valuation }) => date !== undefined && valuation !== undefined)
      .map((point) => ({
        value: parseFloat(point.valuation),
        date: new Date(point.date),
      }));
  };

  const valuationSet = (tokenValuation) => {
    setPoints(mapValuationPoints(tokenValuation));
  };

  const filterDate = async (range) => {
    if (range.start !== null && range.end !== null) {
      const from = range.start + `T00:00:00Z`;
      const to = range.end + `T23:59:59Z`;
      const filteredValuation = await TokenService.getTokenValuation({
        tokenId: token.id,
        from: from,
        to: to,
      });
      valuationSet(filteredValuation);
    }
  };

  useEffect(() => {
    if (!valuation || !valuation.history || valuation.history.length === 0) return;
    valuationSet(valuation);
    setSparklinePoints(mapValuationPoints(valuation));
    setMinDate(valuation.history[valuation.history.length - 1].date.substring(0, 10));
    setMaxDate(valuation.history[0].date.substring(0, 10));
  }, [valuation]);

  return (
    <>
      <div className="_grid _p:x(2) _my-3 _mx-2">
        <ul className="_col(12) _nogutter _col:lg(6)">
          <li className="_flex:justify(space-between) _flex:center">
            <div className="_text:xs _text-coolGrey-400">{t('shared.tokenName')}</div>
            <div className="_text:sm _text-coolGrey-500">{token ? token.name : '-'}</div>
          </li>
          <li className="_flex:justify(space-between) _flex:center">
            <div className="_text:xs _text-coolGrey-400">{t('shared.tokenNetwork')}</div>
            <div className="_text:sm _text-coolGrey-500">
              {token ? (
                <div className="_flex _flex-align-center">
                  <DsLogo
                    name={ethereumNetworkInfo.get(token.blockchainNetwork).logo}
                    width="14px"
                    height="14px"
                    className="_mright-2"
                  />
                  <span>{ethereumNetworkInfo.get(token.blockchainNetwork).name}</span>
                </div>
              ) : (
                '-'
              )}
            </div>
          </li>

          <li className="_flex:justify(space-between) _flex:center">
            {' '}
            <div className="_text:xs _text-coolGrey-400">{t('shared.tokenAddress')}</div>
            <div>
              {token ? (
                <DsExplorerLink
                  address={token.address}
                  network={token.blockchainNetwork}
                  type="token"
                />
              ) : (
                '-'
              )}
            </div>
          </li>
        </ul>
        <ul className="_col(12) _nogutter _col:lg(6)">
          <li className="_flex:justify(space-between) _flex:center">
            <div className="_text:xs _text-coolGrey-400">
              {t('shared.token')} {t('shared.status')}
            </div>
            {token ? (
              token.isPaused ? (
                <DsTag version="default" icon="token" label={t('shared.paused')} />
              ) : (
                <DsTag version="success" icon="token" label={t('shared.active')} />
              )
            ) : (
              <div>-</div>
            )}
          </li>
          <li
            className="_flex:justify(space-between) _flex:center"
            style={{ paddingTop: 'calc( var(--gw) * 0.85 )' }}
          >
            {' '}
            <div className="_text:xs _text-coolGrey-400">{t('shared.valuation')}</div>{' '}
            {hasValuation(token) ? (
              <div className="_flex:center">
                {valuation?.history?.length > 5 ? (
                  <div
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      navDialogRef.current.open();
                      setIsNavOpen(true);
                    }}
                  >
                    <DsSparkline points={sparklinePoints} width="4rem" height="1rem" />
                    <DsIcon
                      name="full-screen"
                      size="1rem"
                      className="_mleft-2"
                      color="var(--coolGrey-400)"
                    />
                  </div>
                ) : (
                  <></>
                )}
                <div className="_text:sm _text-coolGrey-500 _mleft-2">
                  {' '}
                  1 {token.symbol} ={' '}
                  {currencyFormat(token.lastValuationPrice.valuation, token.currency)}
                </div>
              </div>
            ) : (
              '-'
            )}
          </li>
          <li className="_flex:justify(space-between) _flex:center">
            <div className="_text:xs _text-coolGrey-400">{t('shared.tokenType')}</div>
            <div className="_text:sm _text-coolGrey-500">{token ? token.type : '-'}</div>
          </li>
        </ul>
      </div>

      <DsDialog isModal={true} ref={navDialogRef} onClosed={() => setIsNavOpen(false)}>
        <div className="_flex:justify(space-between)">
          {isNavOpen ? (
            <>
              <div className="_flex-align-center _mbottom-4">
                <img
                  src={token.image || placeholderImage}
                  alt={t('shared.tokenLogo')}
                  height={29}
                  className="_m:right(2)"
                />
                <div className="_flex:column ">
                  <h3 className="h6 _fontw-500">{token.symbol}</h3>
                  <span className="_text:xs _text-coolGrey-500 _p:right(3)">{token.name} </span>
                </div>
              </div>
              <DsInputDateRange
                name="valuationRange"
                isFloatLabel
                isMini
                min={minDate}
                max={maxDate}
                initialPosition={valuation.history[0].date.substring(0, 10)}
                value={dateRange}
                onDsInput={({ detail }) => {
                  setDateRange(detail);
                  filterDate(detail);
                }}
              />
            </>
          ) : (
            ''
          )}
        </div>

        <DsLineOverTime
          ref={navDialogRef}
          points={points}
          currency={token.currency}
          width="36rem"
          height="14.5rem"
          color="var(--primary)"
          fill="var(--primary)"
        />
      </DsDialog>
    </>
  );
};

export default DashboardTokenInformation;
